import { useRef, type MouseEvent as ReactMouseEvent } from 'react'
import FloatingMenu from './FloatingMenu'

interface Props {
  /** The record's name, used for the trigger's accessible name. */
  itemName: string
  open: boolean
  /** Toggle this row's menu. Open state is owned by the table so opening one
   *  row's menu closes any other — the trigger swallows the document-level
   *  mousedown that FloatingMenu would otherwise use to close the old one. */
  onToggle: () => void
  onClose: () => void
  onCreateTopic: () => void
}

// Per-row actions menu for the in-meeting tables (task #644 for the
// Scoreboard, task #798 for Rock Review). It lives next to editable inputs,
// a drag handle and buttons that open the row's detail, so every pointer
// event it handles is stopped from reaching the row — otherwise a click here
// would also start a drag or open a modal underneath.
export default function RowActionsMenu({ itemName, open, onToggle, onClose, onCreateTopic }: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null)

  const stop = (e: ReactMouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div style={{ position: 'relative' }} onMouseDown={stop} onClick={stop}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Actions for ${itemName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={stop}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onToggle()
        }}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#351E1D]/10 text-text-muted hover:text-text-primary transition-colors"
      >
        <svg className="w-4 h-4 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      <FloatingMenu
        open={open}
        onClose={onClose}
        anchorRef={triggerRef}
        width={192}
        className="w-48 bg-white rounded-lg shadow-elevated border border-stone/20 py-1"
        onClick={stop}
      >
        <div role="menu" aria-label={`Actions for ${itemName}`}>
          <button
            type="button"
            role="menuitem"
            onMouseDown={stop}
            onClick={(e) => {
              e.stopPropagation()
              onClose()
              onCreateTopic()
            }}
            className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/50 transition-colors"
          >
            Create Topic
          </button>
        </div>
      </FloatingMenu>
    </div>
  )
}
